import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Tabs,
  Tab,
  Box
} from '@material-ui/core'
import {
  ArrowBack,
  ListAlt,
  Build,
  Assessment,
  CloudUpload
} from '@material-ui/icons'
import { Description as DescriptionIcon } from '@material-ui/icons'
import SummaryCards from './SummaryCards'
import SectionBreakdown from './SectionBreakdown'
import ActionItems from './ActionItems'
import RecentActivity from './RecentActivity'
import BidResponseUploadModal from '../BidResponse/BidResponseUploadModal'
import UploadDropzone from './UploadDropzone'

const statusChipProps = (status) => {
  switch (status) {
    case 'draft':
      return { label: 'Draft', chipStyle: { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' } }
    case 'estimating':
      return { label: 'Estimating', chipStyle: { backgroundColor: '#66bb6a', color: '#fff' } }
    case 'review':
      return { label: 'Review', chipStyle: { backgroundColor: '#ffb74d', color: '#fff' } }
    case 'submitted':
      return { label: 'Submitted', chipStyle: { backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff', border: '1px solid rgba(255,255,255,0.5)' } }
    case 'won':
      return { label: 'Won', chipStyle: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'lost':
      return { label: 'Lost', chipStyle: { backgroundColor: '#f44336', color: '#fff' } }
    default:
      return { label: status || 'Draft', chipStyle: { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' } }
  }
}

const navButtonStyle = {
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.4)',
  textTransform: 'none',
  fontSize: 12
}

const cardPanelStyle = {
  backgroundColor: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: 16
}

const ProjectDashboard = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [dashboard, setDashboard] = React.useState(null)
  const [activity, setActivity] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [buildingTab, setBuildingTab] = React.useState(0)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [bidPackages, setBidPackages] = React.useState([])

  React.useEffect(() => {
    fetchDashboard()
    fetchActivity()
    fetchBidPackages()
  }, [projectId])

  const fetchBidPackages = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/bid-packages`).then(res => {
      setBidPackages(res.data)
    }).catch(err => {
      console.error('Failed to fetch bid packages:', err)
    })
  }

  const fetchDashboard = () => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/dashboard`).then(res => {
      setDashboard(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const fetchActivity = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/activity`).then(res => {
      setActivity(res.data)
    }).catch(err => {
      console.error('Failed to fetch activity:', err)
    })
  }

  if (loading || !dashboard) {
    return (
      <div style={{ backgroundColor: '#fafafa', minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  const { project, summary, byBuilding, bySection, vendorQuoteSummary } = dashboard
  const buildings = project?.buildings || []
  const buildingNames = buildings.map(b => b.name || b)
  const selectedBuilding = buildingTab === 0 ? null : buildingNames[buildingTab - 1] || null
  const chipProps = statusChipProps(project?.status)

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100%' }}>
      {/* ── Green Header Bar ────────────────────────────────────────── */}
      <Box style={{
        backgroundColor: '#2e7d32',
        color: '#fff',
        padding: '12px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => history.push('/bid-estimation')}
            style={{ color: '#fff', textTransform: 'none' }}
          >
            Back
          </Button>
          <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
            {project?.name || 'Project Dashboard'}
          </Typography>
          <Chip
            size="small"
            label={chipProps.label}
            style={chipProps.chipStyle}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="outlined" size="small" startIcon={<ListAlt />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/items`)}>
            Line Items
          </Button>
          <Button variant="outlined" size="small" startIcon={<Build />} style={navButtonStyle}
            onClick={() => history.push('/bid-estimation/rates')}>
            Rate Tables
          </Button>
          <Button variant="outlined" size="small" startIcon={<Assessment />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/output`)}>
            Output
          </Button>
          <Button variant="outlined" size="small" startIcon={<CloudUpload />} style={navButtonStyle}
            onClick={() => history.push(`/bid-estimation/${projectId}/vendor-quotes`)}>
            Vendor Quotes
          </Button>
          <Button variant="outlined" size="small" startIcon={<DescriptionIcon />} style={navButtonStyle}
            onClick={() => setShowUploadModal(true)}>
            Import Bid Response
          </Button>
          <UploadDropzone
            projectId={projectId}
            url={props.url}
            onUploadComplete={(result) => {
              // Refresh dashboard data after upload
              fetchDashboard()
              fetchActivity()
              // If vendor quote was parsed, navigate to review
              if (result?.action === 'open_quote_review' && result?.vendorQuote?._id) {
                history.push(`/bid-estimation/${projectId}/vendor-quotes`)
              }
            }}
          />
        </div>
      </Box>

      {/* ── Content Area ────────────────────────────────────────────── */}
      <div style={{ padding: 20 }}>
        {/* KPI Summary Cards */}
        <Box style={{ marginBottom: 20 }}>
          <SummaryCards summary={summary} />
        </Box>

        {/* Building Tabs */}
        <Box style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: 20
        }}>
          <Tabs
            value={buildingTab}
            onChange={(e, newVal) => setBuildingTab(newVal)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" />
            {buildingNames.map(name => (
              <Tab key={name} label={name} />
            ))}
          </Tabs>
        </Box>

        {/* Two-Column Grid: Section Breakdown + Sidebar */}
        <Grid container spacing={2}>
          {/* Left: Section Breakdown (2/3) */}
          <Grid item xs={12} md={8}>
            <div style={cardPanelStyle}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                Section Breakdown
              </Typography>
              <SectionBreakdown
                bySection={bySection}
                byBuilding={byBuilding}
                selectedBuilding={selectedBuilding}
              />
            </div>
          </Grid>

          {/* Right: Sidebar (1/3) */}
          <Grid item xs={12} md={4}>
            <ActionItems
              projectId={projectId}
              summary={summary}
              bySection={bySection}
              vendorQuoteSummary={vendorQuoteSummary}
            />
            <div style={{ height: 16 }} />
            <RecentActivity activity={activity} />
          </Grid>
        </Grid>

        {/* Bid Response Packages */}
        {bidPackages.length > 0 && (
          <Box style={{ marginTop: 20 }}>
            <div style={cardPanelStyle}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                Bid Response Packages
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {bidPackages.map(pkg => (
                  <Chip
                    key={pkg._id}
                    label={`${pkg.name} (${pkg.status})`}
                    clickable
                    color={pkg.status === 'mapped' ? 'primary' : 'default'}
                    variant={pkg.status === 'parse_failed' ? 'outlined' : 'default'}
                    onClick={() => history.push(`/bid-estimation/${projectId}/bid-response/${pkg._id}`)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </div>
            </div>
          </Box>
        )}
      </div>

      {/* Upload Modal */}
      <BidResponseUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={(packageId) => {
          setShowUploadModal(false)
          history.push(`/bid-estimation/${projectId}/bid-response/${packageId}`)
        }}
        projectId={projectId}
        url={props.url}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ProjectDashboard)
